import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    InputAdornment,
    Alert,
    Tabs,
    Tab,
    Grid,
    Divider
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { vortxColors } from '@vortx/shared-ui';
import TOUBuilder from './TOUBuilder';
import TierEditor from './TierEditor';

// Default TOU structure
const DEFAULT_TOU_DATA = {
    seasons: [
        { id: 'summer', name: 'Summer', months: [6, 7, 8, 9], color: '#ff9800' },
        { id: 'winter', name: 'Winter', months: [1, 2, 3, 4, 5, 10, 11, 12], color: '#2196f3' }
    ],
    periods: [
        { id: 'on-peak', name: 'On-Peak', energy: 0.25, demand: 25, seasonId: 'summer' },
        { id: 'mid-peak', name: 'Mid-Peak', energy: 0.15, demand: 15, seasonId: 'summer' },
        { id: 'off-peak', name: 'Off-Peak', energy: 0.08, demand: 0, seasonId: 'summer' },
        { id: 'winter-mid', name: 'Mid-Peak', energy: 0.12, demand: 10, seasonId: 'winter' },
        { id: 'winter-off', name: 'Off-Peak', energy: 0.07, demand: 0, seasonId: 'winter' }
    ],
    windows: [],
    fixedMonthlyFee: '',
    facilityDemand: ''
};

// Default tiered structure
const DEFAULT_TIERS = [
    { max_usage: 500, rate: 0.10 },
    { max_usage: 1000, rate: 0.12 },
    { max_usage: null, rate: 0.15 }
];

function TabPanel({ children, value, index, ...other }) {
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

export default function ElectricTariffBuilder({ open, onSave, onCancel, initialData }) {
    // Initialize state from initialData if provided (for AI extraction pre-fill)
    const getInitialValue = (path, defaultValue = '') => {
        if (!initialData) return defaultValue;
        const data = Array.isArray(initialData) ? initialData[0] : initialData;
        if (!data) return defaultValue;

        // Handle nested paths like 'rate_data.voltage_level'
        const parts = path.split('.');
        let value = data;
        for (const part of parts) {
            if (value && typeof value === 'object') {
                value = value[part];
            } else {
                return defaultValue;
            }
        }
        return value !== undefined && value !== null ? value : defaultValue;
    };

    // Build initial TOU data from AI extraction
    const buildInitialTouData = () => {
        if (!initialData) return DEFAULT_TOU_DATA;
        const data = Array.isArray(initialData) ? initialData[0] : initialData;
        if (!data) return DEFAULT_TOU_DATA;

        // Check if we have TOU structure from AI extraction
        if (data.seasons && data.periods) {
            return {
                seasons: data.seasons || DEFAULT_TOU_DATA.seasons,
                periods: data.periods || DEFAULT_TOU_DATA.periods,
                windows: data.windows || [],
                schedule: data.schedule || null,
                fixedMonthlyFee: data.fixedMonthlyFee || '',
                facilityDemand: data.facilityDemand || ''
            };
        }
        return DEFAULT_TOU_DATA;
    };

    // Determine initial rate mode from extracted data
    const getInitialRateMode = () => {
        if (!initialData) return 0;
        const data = Array.isArray(initialData) ? initialData[0] : initialData;
        if (!data) return 0;

        // If we have TOU structure, default to TOU mode
        if (data.seasons && data.periods && data.periods.length > 0) {
            return 2; // TOU
        }
        if (data.tiers && data.tiers.length > 0) {
            return 1; // Tiered
        }
        return 0; // Simple
    };

    // Metadata
    const [tariffName, setTariffName] = useState(
        getInitialValue('name') || getInitialValue('tariff_code') || ''
    );
    const [utilityName, setUtilityName] = useState(getInitialValue('utility_name'));
    const [rateSchedule, setRateSchedule] = useState(
        getInitialValue('tariff_code') || getInitialValue('rate_schedule_name') || ''
    );
    const [voltageLevel, setVoltageLevel] = useState(getInitialValue('voltage_level'));
    const [option, setOption] = useState(getInitialValue('option'));

    // Rate mode
    const [rateMode, setRateMode] = useState(getInitialRateMode()); // 0=simple, 1=tiered, 2=tou

    // Simple/Blended rates
    const [blendedEnergyRate, setBlendedEnergyRate] = useState('');
    const [blendedDemandRate, setBlendedDemandRate] = useState('');
    const [fixedCharge, setFixedCharge] = useState(
        getInitialValue('fixedMonthlyFee') || getInitialValue('fixed_charge') || ''
    );

    // Tiered rates
    const [tiers, setTiers] = useState(DEFAULT_TIERS);
    const [tieredDemandRate, setTieredDemandRate] = useState('');

    // TOU rates
    const [touData, setTouData] = useState(buildInitialTouData());

    const [error, setError] = useState('');

    const getRateModeString = () => {
        switch (rateMode) {
            case 0: return 'simple';
            case 1: return 'tiered';
            case 2: return 'tou';
            default: return 'simple';
        }
    };

    const handleSave = () => {
        setError('');

        // Validate required fields
        if (!tariffName.trim()) {
            setError('Tariff name is required');
            return;
        }

        if (!utilityName.trim()) {
            setError('Utility name is required');
            return;
        }

        // Build rate_data based on mode
        let rateData = {
            voltage_level: voltageLevel,
            option: option
        };

        const rateModeStr = getRateModeString();

        if (rateModeStr === 'simple') {
            rateData = {
                ...rateData,
                blendedEnergyRate: parseFloat(blendedEnergyRate) || 0,
                blendedDemandRate: parseFloat(blendedDemandRate) || 0,
                fixed_charge: parseFloat(fixedCharge) || 0
            };
        } else if (rateModeStr === 'tiered') {
            // Validate tiers
            for (let i = 0; i < tiers.length; i++) {
                if (tiers[i].rate < 0) {
                    setError(`Tier ${i + 1}: Rate cannot be negative`);
                    return;
                }
            }
            rateData = {
                ...rateData,
                energyTiers: tiers,
                tiers: tiers,
                blendedDemandRate: parseFloat(tieredDemandRate) || 0,
                fixed_charge: parseFloat(fixedCharge) || 0
            };
        } else if (rateModeStr === 'tou') {
            // Validate TOU has at least one period
            if (!touData.periods || touData.periods.length === 0) {
                setError('TOU tariff must have at least one rate period');
                return;
            }
            rateData = {
                ...rateData,
                touData: touData,
                seasons: touData.seasons,
                periods: touData.periods,
                windows: touData.windows,
                fixedMonthlyCharge: touData.fixedMonthlyFee,
                facilityDemand: touData.facilityDemand
            };
        }

        const tariffData = {
            name: tariffName,
            utility_name: utilityName,
            rate_schedule_name: rateSchedule,
            tariff_type: 'electricity',
            rate_mode: rateModeStr,
            rate_data: rateData
        };

        onSave(tariffData);
        handleClose();
    };

    const handleClose = () => {
        // Reset form
        setTariffName('');
        setUtilityName('');
        setRateSchedule('');
        setVoltageLevel('');
        setOption('');
        setRateMode(0);
        setBlendedEnergyRate('');
        setBlendedDemandRate('');
        setFixedCharge('');
        setTiers(DEFAULT_TIERS);
        setTieredDemandRate('');
        setTouData(DEFAULT_TOU_DATA);
        setError('');
        onCancel();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
            <DialogTitle>Create Electric Tariff</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
                        {error}
                    </Alert>
                )}

                {/* Metadata Section */}
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Tariff Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Tariff Name"
                                value={tariffName}
                                onChange={(e) => setTariffName(e.target.value)}
                                required
                                fullWidth
                                placeholder="e.g., SCE TOU-8-B"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Utility Name"
                                value={utilityName}
                                onChange={(e) => setUtilityName(e.target.value)}
                                required
                                fullWidth
                                placeholder="e.g., Southern California Edison"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Rate Schedule"
                                value={rateSchedule}
                                onChange={(e) => setRateSchedule(e.target.value)}
                                fullWidth
                                placeholder="e.g., TOU-8-B"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Voltage Level"
                                value={voltageLevel}
                                onChange={(e) => setVoltageLevel(e.target.value)}
                                fullWidth
                                placeholder="e.g., Secondary, Primary"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Option"
                                value={option}
                                onChange={(e) => setOption(e.target.value)}
                                fullWidth
                                placeholder="e.g., Option A, Option B"
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Rate Structure Section */}
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Rate Structure
                </Typography>

                <Tabs
                    value={rateMode}
                    onChange={(e, v) => setRateMode(v)}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .Mui-selected': { color: vortxColors.gold },
                        '& .MuiTabs-indicator': { backgroundColor: vortxColors.gold }
                    }}
                >
                    <Tab label="Simple (Blended)" />
                    <Tab label="Tiered (Block)" />
                    <Tab label="Time-of-Use (TOU)" />
                </Tabs>

                {/* Simple/Blended Rate Tab */}
                <TabPanel value={rateMode} index={0}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Use simple annual averages for energy and demand charges. Best for quick estimates.
                    </Alert>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Blended Energy Rate"
                                type="number"
                                value={blendedEnergyRate}
                                onChange={(e) => setBlendedEnergyRate(e.target.value)}
                                fullWidth
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    endAdornment: <InputAdornment position="end">/kWh</InputAdornment>
                                }}
                                inputProps={{ step: 0.001 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Blended Demand Rate"
                                type="number"
                                value={blendedDemandRate}
                                onChange={(e) => setBlendedDemandRate(e.target.value)}
                                fullWidth
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    endAdornment: <InputAdornment position="end">/kW</InputAdornment>
                                }}
                                inputProps={{ step: 0.01 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Fixed Monthly Charge"
                                type="number"
                                value={fixedCharge}
                                onChange={(e) => setFixedCharge(e.target.value)}
                                fullWidth
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    endAdornment: <InputAdornment position="end">/month</InputAdornment>
                                }}
                                inputProps={{ step: 0.01 }}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tiered Rate Tab */}
                <TabPanel value={rateMode} index={1}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Define usage tiers with different energy rates per kWh. Common for residential tariffs.
                    </Alert>
                    <TierEditor
                        tiers={tiers}
                        onChange={setTiers}
                    />
                    <Box sx={{ mt: 3 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Flat Demand Rate"
                                    type="number"
                                    value={tieredDemandRate}
                                    onChange={(e) => setTieredDemandRate(e.target.value)}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                        endAdornment: <InputAdornment position="end">/kW</InputAdornment>
                                    }}
                                    helperText="Applied alongside tiered energy rates"
                                    inputProps={{ step: 0.01 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Fixed Monthly Charge"
                                    type="number"
                                    value={fixedCharge}
                                    onChange={(e) => setFixedCharge(e.target.value)}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                        endAdornment: <InputAdornment position="end">/month</InputAdornment>
                                    }}
                                    inputProps={{ step: 0.01 }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </TabPanel>

                {/* TOU Rate Tab */}
                <TabPanel value={rateMode} index={2}>
                    <TOUBuilder
                        touData={touData}
                        onChange={setTouData}
                        readOnly={false}
                    />
                </TabPanel>
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    sx={{
                        bgcolor: vortxColors.gold,
                        color: '#000',
                        '&:hover': { bgcolor: alpha(vortxColors.gold, 0.8) }
                    }}
                >
                    Save Tariff
                </Button>
            </DialogActions>
        </Dialog>
    );
}
